import React, { FC, RefObject } from 'react';
import { SloQualityResultItemDTOStatusEnum } from '@atlassiansox/phobos-client-ts';
import ChevronRightIcon from '@atlaskit/icon/glyph/chevron-right';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import Button from '@atlaskit/button';
import SectionMessage from '@atlaskit/section-message';
import Badge from '@atlaskit/badge';
import pluralize from 'pluralize';

import ExternalLinkAction from '@/components/primitive/external-link';
import {
  flexStart,
  flexWrapNoWrap,
  spaceTopCompact,
  tableExpandContentStyles,
  tableExpandContentInnerStyles,
  tableExpandButtonStyles,
  spaceTopMinimum,
  flex,
  flexDirectionColumn,
  spaceGapComfortable,
} from '@/styles';
import { formatDateForDisplay } from '@/utils';
import { DateDisplayFormats } from '@/constants';

import * as styles from './styles';
import SloQualityLozenge from './slo-quality-lozenge';
import QualityScoreCheckMetadata from './quality-score-check-metadata';
import { SloQualityResultItemDisplayType } from './types';

type QualityScoreCheckResultItemProps = {
  result: SloQualityResultItemDisplayType;
  onExpand: (id: string) => void;
  expanded: boolean;
  refSummary: RefObject<HTMLDivElement>;
};

const QualityScoreCheckResultItem: FC<QualityScoreCheckResultItemProps> = props => {
  const {
    result: {
      title,
      qualityScore,
      severity,
      hasQualityIssue,
      status,
      qualityIssueGuidelineUrl,
      metaData,
      description,
      createdAt,
      publicId,
      checkFrequencyInDays,
    },
    refSummary,
    expanded,
    onExpand,
  }: QualityScoreCheckResultItemProps = props;

  if (!publicId) return <></>;

  const hasWarning: boolean = !!metaData?.warning;
  const showItemWarning: boolean =
    status === SloQualityResultItemDTOStatusEnum.Success && !hasQualityIssue && hasWarning;
  const showItemIssue: boolean =
    status === SloQualityResultItemDTOStatusEnum.Success && hasQualityIssue;
  const showItemSuccess: boolean =
    status === SloQualityResultItemDTOStatusEnum.Success && !hasQualityIssue && !hasWarning;

  return (
    <li>
      <div css={[styles.resultRowStyles, styles.resultItemStyles]}>
        <div css={[flexStart, flexWrapNoWrap]}>
          <span css={tableExpandButtonStyles}>
            <Button
              spacing="none"
              appearance="subtle"
              onClick={() => {
                onExpand(expanded ? '' : publicId);
                try {
                  refSummary.current?.scrollIntoView({ behavior: 'smooth' });
                } catch (e) {
                  // might be called from other places but it is ok to ignore
                }
              }}
              testId="slo-quality-result-item-expand-buttons"
            >
              {expanded && <ChevronDownIcon size="medium" label="" />}
              {!expanded && <ChevronRightIcon size="medium" label="" />}
            </Button>
          </span>
          <span css={styles.itemTitleStyles}>{title}</span>
        </div>
        <div>
          {qualityScore === 10 && <SloQualityLozenge appearance="success">10</SloQualityLozenge>}
          {qualityScore && qualityScore < 10 && (
            <SloQualityLozenge appearance="removed">{qualityScore}</SloQualityLozenge>
          )}
        </div>
        <div>
          <SloQualityLozenge>{severity}</SloQualityLozenge>
        </div>
        <div>
          {showItemIssue && <SloQualityLozenge appearance="moved">issue</SloQualityLozenge>}
          {showItemSuccess && <SloQualityLozenge appearance="success">success</SloQualityLozenge>}
          {showItemWarning && <SloQualityLozenge appearance="moved">warning</SloQualityLozenge>}
        </div>
        <div>
          {(showItemIssue || hasWarning) && (
            <ExternalLinkAction text="View Guide" href={qualityIssueGuidelineUrl} />
          )}
        </div>
      </div>
      {expanded && (
        <div css={[tableExpandContentStyles, spaceTopCompact]}>
          <div
            css={[
              tableExpandContentInnerStyles,
              styles.itemContentStyles,
              spaceGapComfortable,
              flex,
              flexDirectionColumn,
            ]}
          >
            {hasWarning && (
              <section>
                <SectionMessage title="Quality check warning" appearance="warning">
                  <p>{metaData?.warning}</p>
                </SectionMessage>
              </section>
            )}
            <section>
              <h5>Description</h5>
              <div css={spaceTopMinimum}>{description}</div>
            </section>
            <div style={{ display: 'flex', gap: '45px', alignItems: 'flex-end' }}>
              <section>
                <h5>Last Check Time (UTC)</h5>
                <div css={spaceTopMinimum}>
                  <code>
                    {createdAt
                      ? formatDateForDisplay(createdAt, DateDisplayFormats.TIME_WITH_NUMERIC_DATE)
                      : 'N/A'}
                  </code>
                </div>
              </section>
              {checkFrequencyInDays != null && (
                <section>
                  <Badge>
                    Checked every {checkFrequencyInDays} {pluralize('days', checkFrequencyInDays)}
                  </Badge>
                </section>
              )}
            </div>
            <section>
              <h5>Metadata</h5>
              <QualityScoreCheckMetadata metaData={metaData} />
            </section>
          </div>
        </div>
      )}
    </li>
  );
};

export default QualityScoreCheckResultItem;
